import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Twitter, Github, Send, Globe } from 'lucide-react';
import { DiscordIcon } from '@/features/auth/components/BrandIcons';
import { useSectionTranslation } from '@/hooks';
import { api, isApiError } from '@/lib/api';
import { footerTranslations } from './translations';

interface FooterLinkData {
  platform: string;
  url: string;
  label: string;
}

type FooterIconProps = { size?: number; className?: string };

const PLATFORM_ICONS: Record<string, (props: FooterIconProps) => ReactNode> = {
  discord: DiscordIcon,
  twitter: Twitter,
  x: Twitter,
  github: Github,
  telegram: Send,
};

const PLATFORM_COLORS: Record<string, string> = {
  discord: 'hover:text-[#5865F2] hover:border-[#5865F2]/30',
  twitter: 'hover:text-sky-400 hover:border-sky-400/30',
  x: 'hover:text-sky-400 hover:border-sky-400/30',
  github: 'hover:text-brand-white hover:border-accent/30',
  telegram: 'hover:text-[#26A5E4] hover:border-[#26A5E4]/30',
};

const DEFAULT_LINKS: FooterLinkData[] = [
  { platform: 'discord', url: 'https://discord.gg/playerso', label: 'Discord' },
  { platform: 'twitter', url: 'https://twitter.com/playerso', label: 'Twitter' },
  { platform: 'github', url: 'https://github.com/playerso', label: 'GitHub' },
];

export function Footer() {
  const t = useSectionTranslation(footerTranslations);
  const [socialLinks, setSocialLinks] = useState<FooterLinkData[]>(DEFAULT_LINKS);

  useEffect(() => {
    api.get<{ footerLinks?: FooterLinkData[] }>('/marketplace/settings').then((res) => {
      if (!isApiError(res) && Array.isArray(res.data?.footerLinks) && res.data!.footerLinks!.length > 0) {
        setSocialLinks(res.data!.footerLinks!);
      }
    }).catch(() => {});
  }, []);

  const sections = [
    {
      key: 'marketplace' as const,
      links: [
        { key: 'browse' as const, href: '/browse' },
        { key: 'sell' as const, href: '/seller' },
        { key: 'sellerDashboard' as const, href: '/seller/dashboard' },
        { key: 'messages' as const, href: '/dashboard/messages' },
      ],
    },
    {
      key: 'account' as const,
      links: [
        { key: 'signIn' as const, href: '/auth/signin' },
        { key: 'signUp' as const, href: '/auth/signup' },
        { key: 'wallet' as const, href: '/dashboard/wallet' },
        { key: 'orders' as const, href: '/dashboard/orders' },
        { key: 'settings' as const, href: '/settings/profile' },
      ],
    },
    {
      key: 'support' as const,
      links: [
        { key: 'help' as const, href: '/support' },
        { key: 'escrow' as const, href: '/support#faq' },
        { key: 'contact' as const, href: '/support#contact' },
      ],
    },
  ];

  return (
    <footer className="border-t border-surface-border bg-surface-card/30">
      <div className="container-main py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Brand column */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 mb-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-accent/10 border border-accent/20 text-accent">
                <ShieldCheck size={18} />
              </div>
              <span className="font-display text-2xl text-brand-white">Playerso</span>
            </div>
            <p className="text-sm text-brand-muted leading-relaxed mb-4">
              {t.brand}
            </p>
            <div className="flex items-center gap-3">
              {socialLinks.map((link) => {
                const Icon = PLATFORM_ICONS[link.platform] ?? Globe;
                const colors = PLATFORM_COLORS[link.platform] ?? PLATFORM_COLORS.github;
                return (
                  <a
                    key={link.platform}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex h-9 w-9 items-center justify-center rounded-lg bg-surface-card border border-surface-border text-brand-muted ${colors} transition-colors`}
                    aria-label={link.label}
                  >
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Link sections */}
          {sections.map((section) => (
            <div key={section.key}>
              <h3 className="text-xs font-semibold uppercase tracking-widest text-brand-muted mb-4">
                {t.sections[section.key]}
              </h3>
              <ul className="space-y-2.5">
                {section.links.map((link) => (
                  <li key={link.key}>
                    <Link
                      to={link.href}
                      className="text-sm text-brand-muted hover:text-brand-white transition-colors"
                    >
                      {t.links[link.key]}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-6 mt-10 border-t border-surface-border">
          <p className="text-xs text-brand-muted">
            © {new Date().getFullYear()} Playerso. {t.copyright}
          </p>
          <p className="text-xs text-brand-muted text-center">
            {t.trademarks}
          </p>
        </div>
      </div>
    </footer>
  );
}
